import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import { FaPaperPlane, FaSearch, FaComments, FaCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Messages.css';
import useForceDesktopViewport from '../hooks/useForceDesktopViewport';

/** @param {'online'|'away'|'offline'} presence */
function formatPresenceLabel(presence, lastActivity) {
  if (presence === 'online') return 'En ligne';
  if (presence === 'offline') return 'Hors ligne';
  if (presence === 'away' && !lastActivity) return 'Inactif récent';
  if (!lastActivity) return 'Hors ligne';
  const raw = lastActivity instanceof Date ? lastActivity.toISOString() : String(lastActivity);
  const last = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  if (Number.isNaN(last.getTime())) return 'Hors ligne';
  const diffMs = Date.now() - last.getTime();
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  if (minutes < 1) return 'En ligne à l’instant';
  if (minutes < 60) return `En ligne il y a ${minutes} min`;
  if (hours < 72) return `En ligne il y a ${hours} h`;
  return 'Vu il y a longtemps';
}

function PresenceDot({ presence, lastActivity, title }) {
  const p = presence || 'offline';
  if (p === 'offline') return null;
  return (
    <span
      className={`online-indicator online-indicator--${p}`}
      title={title ?? formatPresenceLabel(p, lastActivity)}
    >
      <FaCircle />
    </span>
  );
}

function PresenceCaption({ presence, lastActivity }) {
  const p = presence || 'offline';
  if (p === 'online') {
    return <span className="presence-caption presence-caption--online"> En ligne</span>;
  }
  return (
    <span className={`presence-caption presence-caption--${p}`}>
      {' '}
      • {formatPresenceLabel(p, lastActivity)}
    </span>
  );
}

const Messages = () => {
  useForceDesktopViewport('messages-page');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isCommercialReceiveOnly = Number(user?.fonction) === 5;
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllUsers, setShowAllUsers] = useState(() => searchParams.get('compose') === '1');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('compose') === '1') {
      setShowAllUsers(true);
    }
  }, [searchParams]);

  // Mettre à jour l'activité de l'utilisateur actuel
  useEffect(() => {
    const updateActivity = async () => {
      try {
        await api.post('/messages/activity');
      } catch (error) {
        // Ignorer les erreurs silencieusement
      }
    };

    // Mettre à jour immédiatement
    updateActivity();

    // Puis toutes les 2 minutes
    const interval = setInterval(updateActivity, 120000);
    return () => clearInterval(interval);
  }, []);

  // Récupérer la liste des conversations
  const { data: conversations, isLoading: loadingConversations } = useQuery(
    'conversations',
    async () => {
      const res = await api.get('/messages/conversations');
      return res.data.data || [];
    },
    {
      refetchInterval: 5000, // Rafraîchir toutes les 5 secondes
    }
  );

  // Récupérer les utilisateurs pour démarrer une nouvelle conversation
  const { data: allUsers, isLoading: loadingUsers } = useQuery(
    'message-users',
    async () => {
      const res = await api.get('/messages/users');
      return res.data.data || [];
    },
    {
      enabled: !isCommercialReceiveOnly,
      refetchInterval: 10000, // Rafraîchir toutes les 10 secondes pour mettre à jour le statut
    }
  );

  // Récupérer les messages de la conversation sélectionnée
  const { data: messages, isLoading: loadingMessages } = useQuery(
    ['messages', selectedUser],
    async () => {
      if (!selectedUser) return [];
      const res = await api.get(`/messages/conversation/${selectedUser.id}`);
      return res.data.data || [];
    },
    {
      enabled: !!selectedUser,
      refetchInterval: 3000, // Rafraîchir toutes les 3 secondes
      onSuccess: () => {
        // Mettre à jour le compteur sidebar (cercle rouge) dès qu'on ouvre une conversation
        queryClient.invalidateQueries('messages-unread-count');
      },
    }
  );

  // Mutation pour envoyer un message
  const sendMessageMutation = useMutation(
    async ({ destination, message }) => {
      const res = await api.post('/messages', { destination, message });
      return res.data;
    },
    {
      onSuccess: () => {
        setMessageText('');
        queryClient.invalidateQueries(['messages', selectedUser]);
        queryClient.invalidateQueries('conversations');
        queryClient.invalidateQueries('messages-unread-count');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi du message');
      },
    }
  );

  // Filtrer les conversations selon le terme de recherche
  const filteredConversations = conversations?.filter(conv =>
    conv.pseudo?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Filtrer les utilisateurs pour nouvelle conversation (hors commercial en lecture seule)
  const filteredUsers = allUsers?.filter(u =>
    u.pseudo?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Créer une liste combinée : conversations existantes + autres utilisateurs
  const allContacts = useMemo(() => {
    const conversationIds = new Set(conversations?.map(c => c.id) || []);
    const otherUsers = allUsers?.filter(u => !conversationIds.has(u.id)) || [];
    
    return [
      ...(conversations || []),
      ...(showAllUsers ? otherUsers : [])
    ];
  }, [conversations, allUsers, showAllUsers]);

  const selectedContact = useMemo(() => {
    if (!selectedUser) return null;
    return allContacts.find((c) => c.id === selectedUser.id) || selectedUser;
  }, [selectedUser, allContacts]);

  // Scroll automatique vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (isCommercialReceiveOnly) return;
    if (!messageText.trim() || !selectedUser) return;

    sendMessageMutation.mutate({
      destination: selectedUser.id,
      message: messageText.trim(),
    });
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getDefaultAvatar = (genre) => {
    return genre === 1 
      ? 'https://ui-avatars.com/api/?name=User&background=e91e63&color=fff&size=128'
      : 'https://ui-avatars.com/api/?name=User&background=2196f3&color=fff&size=128';
  };

  return (
    <div className="messages-container">
      <div className="messages-sidebar">
        <div className="messages-header">
          <h2>
            <FaComments /> Messages
          </h2>
          <div className="messages-search">
            <FaSearch />
            <input
              type="text"
              placeholder="Rechercher une conversation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="conversations-list">
          {loadingConversations && !isCommercialReceiveOnly && loadingUsers ? (
            <div className="loading">Chargement...</div>
          ) : loadingConversations ? (
            <div className="loading">Chargement...</div>
          ) : (
            <>
              {/* Bouton pour afficher/masquer tous les utilisateurs */}
              {!searchTerm && !isCommercialReceiveOnly && (
                <button
                  className="toggle-users-button"
                  onClick={() => setShowAllUsers(!showAllUsers)}
                >
                  {showAllUsers ? 'Masquer' : 'Afficher'} tous les utilisateurs
                </button>
              )}

              {/* Liste des contacts */}
              {searchTerm ? (
                isCommercialReceiveOnly ? (
                  filteredConversations.length === 0 ? (
                    <div className="no-conversations">Aucune conversation trouvée</div>
                  ) : (
                    filteredConversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={`conversation-item ${selectedUser?.id === conv.id ? 'active' : ''}`}
                        onClick={() => handleSelectUser(conv)}
                      >
                        <div className="conversation-avatar">
                          <img
                            src={conv.photo || getDefaultAvatar(conv.genre)}
                            alt={conv.pseudo}
                            onError={(e) => {
                              e.target.src = getDefaultAvatar(conv.genre);
                            }}
                          />
                          <PresenceDot presence={conv.presence} lastActivity={conv.last_activity} />
                          {conv.unread_count > 0 && (
                            <span className="unread-badge">{conv.unread_count}</span>
                          )}
                        </div>
                        <div className="conversation-info">
                          <div className="conversation-header">
                            <span className="conversation-name">
                              {conv.pseudo}
                              <PresenceCaption presence={conv.presence} lastActivity={conv.last_activity} />
                            </span>
                            {conv.last_message_date && (
                              <span className="conversation-time">{formatDate(conv.last_message_date)}</span>
                            )}
                          </div>
                          <div className="conversation-preview">
                            {conv.last_message && (
                              <span className="last-message">
                                {conv.last_message.length > 50
                                  ? `${conv.last_message.substring(0, 50)}...`
                                  : conv.last_message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                // Mode recherche : afficher les résultats filtrés
                filteredUsers.length === 0 ? (
                  <div className="no-conversations">Aucun résultat</div>
                ) : (
                  filteredUsers.map((u) => {
                    const isConversation = conversations?.some(c => c.id === u.id);
                    const conv = isConversation ? conversations.find(c => c.id === u.id) : null;
                    const contact = conv || u;
                    
                    return (
                      <div
                        key={contact.id}
                        className={`conversation-item ${selectedUser?.id === contact.id ? 'active' : ''} ${!isConversation ? 'new-conversation' : ''}`}
                        onClick={() => handleSelectUser(contact)}
                      >
                        <div className="conversation-avatar">
                          <img
                            src={contact.photo || getDefaultAvatar(contact.genre)}
                            alt={contact.pseudo}
                            onError={(e) => {
                              e.target.src = getDefaultAvatar(contact.genre);
                            }}
                          />
                          <PresenceDot presence={contact.presence} lastActivity={contact.last_activity} />
                          {conv && conv.unread_count > 0 && (
                            <span className="unread-badge">{conv.unread_count}</span>
                          )}
                        </div>
                        <div className="conversation-info">
                          <div className="conversation-header">
                            <span className="conversation-name">
                              {contact.pseudo}
                              <PresenceCaption
                                presence={contact.presence}
                                lastActivity={contact.last_activity}
                              />
                            </span>
                            {conv && conv.last_message_date && (
                              <span className="conversation-time">
                                {formatDate(conv.last_message_date)}
                              </span>
                            )}
                          </div>
                          <div className="conversation-preview">
                            {conv && conv.last_message && (
                              <span className="last-message">
                                {conv.last_message.length > 50
                                  ? `${conv.last_message.substring(0, 50)}...`
                                  : conv.last_message}
                              </span>
                            )}
                            {contact.fonction_titre && (
                              <span className="conversation-role">{contact.fonction_titre}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )
                )
              ) : (
                // Mode normal : afficher les conversations + optionnellement tous les utilisateurs
                allContacts.length === 0 ? (
                  <div className="no-conversations">Aucune conversation</div>
                ) : (
                  <>
                    {conversations && conversations.length > 0 && (
                      <>
                        {conversations.map((conv) => {
                          const userInfo = allUsers?.find(u => u.id === conv.id);
                          return (
                            <div
                              key={conv.id}
                              className={`conversation-item ${selectedUser?.id === conv.id ? 'active' : ''}`}
                              onClick={() => handleSelectUser(conv)}
                            >
                              <div className="conversation-avatar">
                                <img
                                  src={conv.photo || getDefaultAvatar(conv.genre)}
                                  alt={conv.pseudo}
                                  onError={(e) => {
                                    e.target.src = getDefaultAvatar(conv.genre);
                                  }}
                                />
                                <PresenceDot
                                  presence={conv.presence || userInfo?.presence}
                                  lastActivity={conv.last_activity || userInfo?.last_activity}
                                />
                                {conv.unread_count > 0 && (
                                  <span className="unread-badge">{conv.unread_count}</span>
                                )}
                              </div>
                              <div className="conversation-info">
                                <div className="conversation-header">
                                  <span className="conversation-name">
                                    {conv.pseudo}
                                    <PresenceCaption
                                      presence={conv.presence || userInfo?.presence}
                                      lastActivity={conv.last_activity || userInfo?.last_activity}
                                    />
                                  </span>
                                  <span className="conversation-time">
                                    {formatDate(conv.last_message_date)}
                                  </span>
                                </div>
                                <div className="conversation-preview">
                                  {conv.last_message && (
                                    <span className="last-message">
                                      {conv.last_message.length > 50
                                        ? `${conv.last_message.substring(0, 50)}...`
                                        : conv.last_message}
                                    </span>
                                  )}
                                  {conv.fonction_titre && (
                                    <span className="conversation-role">{conv.fonction_titre}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {showAllUsers && allUsers && (
                      <>
                        {conversations && conversations.length > 0 && (
                          <div className="conversations-divider">Autres utilisateurs</div>
                        )}
                        {allUsers
                          .filter(u => !conversations?.some(c => c.id === u.id))
                          .map((u) => (
                            <div
                              key={u.id}
                              className={`conversation-item new-conversation ${selectedUser?.id === u.id ? 'active' : ''}`}
                              onClick={() => handleSelectUser(u)}
                            >
                              <div className="conversation-avatar">
                                <img
                                  src={u.photo || getDefaultAvatar(u.genre)}
                                  alt={u.pseudo}
                                  onError={(e) => {
                                    e.target.src = getDefaultAvatar(u.genre);
                                  }}
                                />
                                <PresenceDot presence={u.presence} lastActivity={u.last_activity} />
                              </div>
                              <div className="conversation-info">
                                <div className="conversation-header">
                                  <span className="conversation-name">
                                    {u.pseudo}
                                    <PresenceCaption presence={u.presence} lastActivity={u.last_activity} />
                                  </span>
                                </div>
                                <div className="conversation-preview">
                                  {u.fonction_titre && (
                                    <span className="conversation-role">{u.fonction_titre}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                      </>
                    )}
                  </>
                )
              )}
            </>
          )}
        </div>
      </div>

      <div className="messages-chat">
        {!selectedUser ? (
          <div className="no-chat-selected">
            <FaComments size={64} />
            <h3>Sélectionnez une conversation</h3>
            <p>Choisissez une conversation dans la liste pour commencer à discuter</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="chat-user-info">
                <div className="chat-user-avatar-wrap">
                  <img
                    src={selectedUser.photo || getDefaultAvatar(selectedUser.genre)}
                    alt={selectedUser.pseudo}
                    onError={(e) => {
                      e.target.src = getDefaultAvatar(selectedUser.genre);
                    }}
                  />
                  <PresenceDot
                    presence={selectedContact?.presence}
                    lastActivity={selectedContact?.last_activity}
                  />
                </div>
                <div>
                  <h3>{selectedUser.pseudo}</h3>
                  <div className={`chat-presence-line chat-presence-line--${selectedContact?.presence || 'offline'}`}>
                    {formatPresenceLabel(selectedContact?.presence, selectedContact?.last_activity)}
                  </div>
                  {selectedUser.fonction_titre && (
                    <span className="chat-user-role">{selectedUser.fonction_titre}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="chat-messages" ref={messagesContainerRef}>
              {loadingMessages ? (
                <div className="loading">Chargement des messages...</div>
              ) : messages?.length === 0 ? (
                <div className="no-messages">
                  {isCommercialReceiveOnly
                    ? 'Aucun message reçu pour le moment.'
                    : 'Aucun message. Commencez la conversation !'}
                </div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.expediteur === user.id;
                  return (
                    <div
                      key={msg.id}
                      className={`message ${isOwn ? 'own' : 'other'}`}
                    >
                      {!isOwn && (
                        <img
                          src={msg.expediteur_photo || getDefaultAvatar(msg.expediteur_genre)}
                          alt={msg.expediteur_pseudo}
                          className="message-avatar"
                          onError={(e) => {
                            e.target.src = getDefaultAvatar(msg.expediteur_genre);
                          }}
                        />
                      )}
                      <div className="message-content">
                        {!isOwn && (
                          <span className="message-sender">{msg.expediteur_pseudo}</span>
                        )}
                        <div className="message-bubble">
                          <p>{msg.message}</p>
                          <span className="message-time">
                            {formatDate(msg.date_modif)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {isCommercialReceiveOnly ? (
              <div className="chat-readonly-notice">
                En tant que commercial, vous pouvez uniquement recevoir des messages.
              </div>
            ) : (
              <form className="chat-input-form" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  className="chat-input"
                  placeholder="Tapez votre message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  disabled={sendMessageMutation.isLoading}
                />
                <button
                  type="submit"
                  className="chat-send-button"
                  disabled={!messageText.trim() || sendMessageMutation.isLoading}
                >
                  <FaPaperPlane />
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Messages;
